import * as React from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import BlogPublishInfo from '../BlogPublishInfo.tsx';
import compoundComponentsPng from "../../static/blog_compound_components_main_image.png";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import ImageListItem from '@mui/material/ImageListItem';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Alert from '@mui/material/Alert';
import { CodeBlock, dracula } from 'react-code-blocks';


function createData(
    row: string,
    contextApi: string,
    redux: string,
  ) {
    return { row, contextApi, redux};
  }

const rows = [
    createData('Architecture', 'Uses providers and consumers to pass data down through the component tree', 'Uses Reducers and Actions'),
    createData('Usage', 'Highly recommended for simple state management', 'For high performance large scale application state management'),
    createData('Robustness', 'Not as robust as Redux', 'highly Robust'),
    createData('Learning Curve', 'Simpler to set up and use, less overhead for simple state management', 'Can have a steeper learning curve and require more boilerplate code for simple use cases'),
  ];

  function BasicTable() {
    return (
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
                <TableCell align="left">Parameter</TableCell>
                <TableCell align="center">Context API</TableCell>
                <TableCell align="center">Redux</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.row}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                
                <TableCell align="left">{row.row}</TableCell>
                <TableCell align="center">{row.contextApi}</TableCell>
                <TableCell align="center">{row.redux}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

const codeParentComponent = `
    import React, { createContext, useContext, useCallback } from 'react';
    import { Snackbar } from 'react-native-paper';

    const GlobalContext = createContext({
        showSnackbar: (msg: string, dur: number) => { },
    });

    export const useGlobalContext = () => useContext(GlobalContext);

    export const GlobalProvider = ({childern}: {children: React.ReactNode}) => {
        const showSnackbar = useCallback((msg: string, dur = 3000) => {
            setMessage(msg);
            setVisible(true);
        }, []);

        return (
            <GlobalContext.Provider value={{ showSnackbar }} >
                {children}
                 <Snackbar
                    visible={visible}
                    onDismiss={() => setVisible(false)}
                >
                    {message}
                </Snackbar>
            </GlobalContext.Provider>
        );
    };
`;

const codeChildComponent = `
import React, { useEffect } from 'react';
import { useGlobalContext } from "@/providers/GlobalProvider";

const ChildComponent = () => {
    const { showSnackbar } = useGlobalContext();
    
    useEffect(()=> {
        showSnackbar('Communicating from child to parent', 3000);
    }, []);
    return (<div>Child Component<div>)
}
`;

const integrationComponent = `
import React, { useEffect } from 'react';

const App = () => {
   return (
    <GlobalProvider>
        <ChildComponent />
    </GlobalProvider>
   )
}
`;

export default function ReactCompoundComponentsBlog05() {
    return (
        <Container id="ReactCompoundComponentsBlog05" sx={{ my: { sm: 0, xs: 5 }}} fixed>
          <Box sx={{  minHeight: '50vh' }}>
              <Typography variant="h1" component="h1" align="center">
                Compound Components Design Pattern in React.js
              </Typography>
              <BlogPublishInfo date="27th May 2025"/>
              <Divider />
              <ImageListItem sx={{ py: { sm: 2, xs: 8 }, px: { sm: 10, xs: 8 }, mx: {sm: '6vw'} }}>
                <img
                    srcSet={compoundComponentsPng}
                    alt={"compoundComponent"}
                    loading="lazy"
                  />
              </ImageListItem>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Overview
              </Typography>
              <Typography variant="body1" align="left">
                Design pattern allowing us to avoid prop drilling and easily communicate between parent and nested child components in react and managing complex application state.
                The shared common funtionality can stay with the parent and the child can have their independent behaviour and state along with the shared state and behaviours from parent.
                Reduces complexity of communication down the children hierarchy and also makes the code more readable using hooks instead of passing props from parent at each level
              </Typography>
              <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                What are Compound Components?
              </Typography>
              <Typography variant="body1" align="left">
                Compound Components are components wherein the parent Manages state and behaviour of Child Components and Child Components interact with Parent using Context webhook
               <br/>
                The ContextAPI is often used in Compound Components but dont always have to be used
              </Typography>
                <br/>
              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Why Compound Components and what is its composition?
              </Typography>
              <Typography variant="body1" align="left">
                <b>What compels us</b> to choose compound components?
                <List sx={{ listStyleType: 'disc', paddingX: {sm: 2, xs: 2} }}>
                <ListItem sx={{ display: 'list-item' }}>
                    The two major aspects of this design pattern include a Parent Component and multiple Child Component(s).
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    Parent Component - Encapsulates the State and Certain behaviours of child components
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    Child Component - Wrapped the Parent Context and also has its own independent state and behaviour and certain behavior is linked to parent
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                        Integration: We wrap the children in the parent component              
                   </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                    Communication - child components uses the useContext hook exposed by the parent component and communicates with its parent
                  </ListItem>
                  <ListItem sx={{ display: 'list-item' }}>
                        Benefits<br />
                        This design pattern gives us the advantage of avoiding prop drilling wherein otherwise we would have had to pass props to children and then the child behaves accordingly.
                        <Alert variant="filled" severity="success">
                        Allows reusability   <br />
                        Offers Flexibility      <br />
                        Seperation of Concern
                            </Alert>
                    
                   </ListItem>
                </List>
              
               The Parent Component with Provider looks something like below:
                <br/><br/>
                <CodeBlock
                  text={codeParentComponent}
                  language={'javascript'}
                  showLineNumbers={true}
                  theme={dracula}
                />
                <br/>

                The child component looks something like below:
                <br/><br/>
                <CodeBlock
                  text={codeChildComponent}
                  language={'javascript'}
                  showLineNumbers={true}
                  theme={dracula}
                />
                <br/>

                The Integration:
                <br/><br/>
                <CodeBlock
                  text={integrationComponent}
                  language={'javascript'}
                  showLineNumbers={true}
                  theme={dracula}
                />
                <br/>
              </Typography>


              <Typography variant="h3" component="h2" align="left" gutterBottom={true}>
                Using Compound Components via Context API vs Redux
              </Typography>
              <Typography variant="body1" align="left">
                 <Alert variant="filled" severity="warning">
                 We should evaluate whether the contextAPI itself suffices for a project based on the performance and scalability requirements of the project.
                 <br />
                 Rather than blindly going ahead with ContextApi for stores like redux first evaluating the pros/cons always helps.
                </Alert>
              </Typography>
              <br />
              <BasicTable />
          </Box>
        </Container>
    )
};